import { itemCouponDraftAtom } from '@activities/order/OrderCouponActivity/atoms';
import useItemCoupons, { toOptionKey } from '@activities/order/OrderCouponActivity/hooks/useItemCoupons';
import Button from '@components/Button';
import Column from '@components/Column';
import Image from '@components/Image';
import RadioCircle from '@components/Radio/RadioCircle';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

declare module '@stackflow/config' {
    interface Register {
        ItemCouponTargetBottomSheet: {
            couponId: string;
        };
    }
}

/** 상품 쿠폰을 적용할 상품 선택 */
const ItemCouponTargetBottomSheet: ActivityComponentType<'ItemCouponTargetBottomSheet'> = ({ params }) => {
    const { couponId } = params;
    const { t } = useTranslation('order');
    const priceUnit = usePriceUnit();
    const { pop } = useFlow();
    const [draft, setDraft] = useAtom(itemCouponDraftAtom);
    const item = useItemCoupons().find((it) => it.coupon.id === couponId);
    const options = item?.options || [];
    // 미리 고르지 않고, 이미 적용한 상품이 있을 때만 선택된 상태로 시작한다
    const [selectedKey, setSelectedKey] = useState<string | undefined>(
        item && draft[couponId] ? toOptionKey(item.coupon, draft[couponId]) : undefined,
    );

    const handleApply = () => {
        const selected = options.find((option) => option.key === selectedKey);
        if (selected) {
            setDraft({ ...draft, [couponId]: selected.target });
        }
        pop();
    };

    return (
        <BottomSheet>
            <Header>
                <Text size='title' fontWeight='bold' color={colors.textPrimary}>
                    {t('itemTarget.title')}
                </Text>
            </Header>
            <Options fullWidth spacing={2}>
                {options.map((option) => {
                    // 같은 상품을 여러 개 담았으면 상품 1개씩 따로 보여준다 (1+1 쿠폰 제외)
                    const { key, line, disabled } = option;
                    const unitPrice = Math.floor(line.totalAmount / line.quantity);
                    const price = item?.coupon.discountMethod === 'BOGO' ? line.totalAmount : unitPrice;
                    return (
                        <Option key={key}
                            aria-disabled={disabled}
                            onClick={() => {
                                if (!disabled) {
                                    setSelectedKey(key);
                                }
                            }}>
                            <Row spacing={4}>
                                <Thumbnail src={line.itemImageUrl} width={50} height={50} alt={line.itemName}/>
                                <Column spacing={2}>
                                    <Text fontWeight='medium'
                                        color={disabled ? colors.textUnselected : colors.textPrimary}>
                                        {line.itemName}
                                    </Text>
                                    <Text fontWeight='medium'
                                        color={disabled ? colors.textUnselected : colors.textPrimary}>
                                        {price.toLocaleString()}{priceUnit}
                                    </Text>
                                </Column>
                            </Row>
                            {!disabled && <RadioCircle isSelected={selectedKey === key} />}
                        </Option>
                    );
                })}
            </Options>
            <Footer spacing={2} fullWidth>
                <Action variant='tone' onClick={pop}>{t('itemTarget.cancel')}</Action>
                <Action disabled={!selectedKey} onClick={handleApply}>{t('itemTarget.apply')}</Action>
            </Footer>
        </BottomSheet>
    );
};

const Header = styled.div`
    align-items: center;
    display: flex;
    height: 64px;
    justify-content: center;
`;

const Options = styled(Column)`
    max-height: 60vh;
    overflow-y: auto;
    padding: 0 var(--spacing-4);
`;

const Option = styled.div`
    align-items: center;
    background-color: var(--color-background-pure);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    padding: var(--spacing-4);
    width: 100%;

    &[aria-disabled="true"] {
        cursor: default;
        opacity: 0.5;
    }
`;

const Thumbnail = styled(Image)`
    border-radius: var(--radius-xs);
    flex-shrink: 0;
    height: 50px;
    object-fit: cover;
    width: 50px;
`;

const Footer = styled(Row)`
    padding: var(--spacing-4) var(--spacing-4) calc(var(--spacing-4) + var(--safe-area-bottom));
`;

const Action = styled(Button)`
    flex: 1;
    padding: 0;
`;

export default ItemCouponTargetBottomSheet;
